import React from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";

import ProtectedRoute from "@/components/ProtectedRoute";
import { AppShell } from "./shell/AppShell";

import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import Dashboard from "@/pages/Dashboard";
import InventoryHome from "@/pages/inventory/InventoryHome";
import CategoriesPage from "@/pages/inventory/Categories";
import WarehousesPage from "@/pages/inventory/Warehouses";
import ProductsPage from "@/pages/inventory/Products";
import StockView from "@/pages/inventory/StockView";
import MovementsPage from "@/pages/inventory/Movements";

import FoundationHome from "./pages/FoundationHome";
import ModulePlaceholder from "./pages/ModulePlaceholder";
import SettingsPage from "./pages/SettingsPage";
import PortalApp from "@/portal/PortalApp";
import SyncPage from "./pages/SyncPage";
import NewInvoicePage from "./pages/NewInvoicePage";
import { PartiesList, ProductsList, StockList, InvoicesList, OrdersList } from "./pages/lists";
import ApprovalsPage from "./pages/ApprovalsPage";

/**
 * Sprint 10 router.
 *
 *   /                       → bounces to /app/dashboard or /login
 *   /login, /register,
 *   /forgot-password        → auth pages (unchanged)
 *   /dashboard, /inventory* → legacy pages (unchanged, backwards compat)
 *   /app/*                  → new AppShell + permission-aware nested routes
 */
function RootRedirect() {
  const { user, initializing } = useAuth();
  if (initializing) return null;
  return <Navigate to={user ? "/app/dashboard" : "/login"} replace />;
}

export function AppRouter() {
  return (
    <Routes>
      <Route path="/" element={<RootRedirect />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/portal/*" element={<PortalApp />} />

      {/* -------- Legacy routes retained unchanged -------- */}
      <Route
        path="/dashboard"
        element={<ProtectedRoute><Dashboard /></ProtectedRoute>}
      />
      <Route path="/inventory" element={<ProtectedRoute><InventoryHome /></ProtectedRoute>} />
      <Route path="/inventory/categories" element={<ProtectedRoute><CategoriesPage /></ProtectedRoute>} />
      <Route path="/inventory/warehouses" element={<ProtectedRoute><WarehousesPage /></ProtectedRoute>} />
      <Route path="/inventory/products" element={<ProtectedRoute><ProductsPage /></ProtectedRoute>} />
      <Route path="/inventory/stock" element={<ProtectedRoute><StockView /></ProtectedRoute>} />
      <Route path="/inventory/movements" element={<ProtectedRoute><MovementsPage /></ProtectedRoute>} />

      {/* -------- Sprint 10 AppShell routes (new foundation) -------- */}
      <Route
        path="/app"
        element={
          <ProtectedRoute>
            <AppShell />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="dashboard" replace />} />
        <Route path="dashboard" element={<FoundationHome />} />
        <Route path="inventory" element={<ProductsList />} />
        <Route path="inventory/stock" element={<StockList />} />
        <Route path="parties" element={<PartiesList />} />
        <Route
          path="orders"
          element={
            <ProtectedRoute perm="order.view">
              <OrdersList />
            </ProtectedRoute>
          }
        />
        <Route
          path="sales"
          element={
            <ProtectedRoute perm="sales.view">
              <InvoicesList />
            </ProtectedRoute>
          }
        />
        <Route path="purchases" element={<ModulePlaceholder module="purchases" />} />
        <Route path="accounts" element={<ModulePlaceholder module="accounts" />} />
        <Route path="reports" element={<ModulePlaceholder module="reports" />} />
        <Route path="ai" element={<ModulePlaceholder module="ai" />} />
        <Route path="sync" element={<SyncPage />} />
        <Route
          path="sales/new-invoice"
          element={
            <ProtectedRoute perm="document.create">
              <NewInvoicePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="approvals"
          element={
            <ProtectedRoute perm={["portal.request.view", "portal.order.approve"]}>
              <ApprovalsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="settings"
          element={
            <ProtectedRoute roles={["admin"]}>
              <SettingsPage />
            </ProtectedRoute>
          }
        />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
